import React from 'react';
import { AlertTriangle, RefreshCw, Wifi, WifiOff } from 'lucide-react';

const FinancialErrorHandler = ({ 
  error, 
  onRetry, 
  loading = false,
  title = "Erro ao carregar dados financeiros",
  showNetworkStatus = true 
}) => {
  const isTimeoutError = error?.message?.includes('timeout') || error?.message?.includes('Request timeout');
  const isNetworkError = error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error');
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-lg font-medium text-red-900 mb-2">
              {title}
            </h3>
            
            {/* Status da rede */}
            {showNetworkStatus && (
              <div className="mb-4 flex items-center space-x-2">
                {navigator.onLine ? (
                  <>
                    <Wifi className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-700">Conectado à internet</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-red-700">Sem conexão com a internet</span>
                  </>
                )}
              </div>
            )}
            
            {/* Mensagem de erro específica */}
            <div className="mb-4">
              {isTimeoutError ? (
                <div className="text-sm text-red-700">
                  <p className="font-medium">⏱️ Timeout da requisição</p>
                  <p className="mt-1">
                    A requisição demorou mais de 30 segundos para responder. 
                    Isso pode indicar problemas de conectividade ou sobrecarga do servidor.
                  </p>
                </div>
              ) : isNetworkError ? (
                <div className="text-sm text-red-700">
                  <p className="font-medium">🌐 Erro de rede</p>
                  <p className="mt-1">
                    Não foi possível conectar ao servidor. Verifique sua conexão com a internet.
                  </p>
                </div>
              ) : (
                <div className="text-sm text-red-700">
                  <p className="font-medium">❌ Erro inesperado</p>
                  <p className="mt-1">
                    {error?.message || 'Ocorreu um erro inesperado ao carregar os dados financeiros.'}
                  </p>
                </div>
              )}
            </div>
            
            {/* Sugestões de solução */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-red-900 mb-2">Sugestões:</h4>
              <ul className="text-sm text-red-700 space-y-1">
                <li>• Verifique sua conexão com a internet</li>
                <li>• Tente recarregar a página</li>
                <li>• Aguarde alguns minutos e tente novamente</li>
                {isTimeoutError && (
                  <li>• O servidor pode estar sobrecarregado - tente mais tarde</li>
                )}
              </ul>
            </div>
            
            {/* Botões de ação */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onRetry}
                disabled={loading}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span>{loading ? 'Tentando novamente...' : 'Tentar novamente'}</span>
              </button>
              
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] text-sm font-medium rounded-md hover:bg-[color:var(--surface)] focus:outline-none focus:ring-2 focus:ring-[color:var(--border)] focus:ring-offset-2"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Recarregar página</span>
              </button>
              
              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] text-sm font-medium rounded-md hover:bg-[color:var(--surface)] focus:outline-none focus:ring-2 focus:ring-[color:var(--border)] focus:ring-offset-2"
              >
                <span>← Voltar</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialErrorHandler;


